import React from "react";
import { Box, IconButton, List, ListItem, ListItemText, makeStyles, Typography } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { Add, Delete, Remove } from "@material-ui/icons";
import { getTotalItemsCount, getCategoryCount, getSubCategoryCount, getCustomItemsCount } from "./InventoryHelper";
import {
    deleteCustomItems,
    deleteInventoryItem,
    updateCustomItemsQty,
    updateInventoryItemQty
} from "../../redux/reducers/inventory/inventoryReducer";

const useStyles = makeStyles((theme) => ({
    searchCategoryItems: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "8px",
        border: "1px solid #BDBDBD",
        borderRadius: "8px",
        backgroundColor: "#FFFFFF",
        paddingTop: 2,
        paddingBottom: 2,
    },
    emptyInventory: {
        fontWeight: 600,
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "300px",
        paddingLeft: "25%",
        paddingRight: "25%",
        [theme.breakpoints.down("sm")]: {
            height: "100%",
            paddingLeft: 0,
            paddingRight: 0,
        }
    },
    iconColor: {
        color: "#000000DE",
    },
    iconBox: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: "#FFCACA",
        borderRadius: 6,
    },
    customItemText: {
        display: "flex",
        alignItems: "center",
        gap: 12,
    },
}));

const InventoryList = () => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const inventoryData = useSelector((state) => state.inventoryInfo);
    const totalCount = getTotalItemsCount(inventoryData);

    const customItemscount = getCustomItemsCount(inventoryData);
    const customItemsList = inventoryData.getIn(["customItems", "items"]);

    const updateQtyFn = (operation, selectedIndex) => {
        const params = {
            operation: operation,
            path: [
                "inventory",
                selectedIndex.mainIndex,
                "category",
                selectedIndex.subIndex,
                "items",
                selectedIndex.itemIndex,
            ],
        };
        dispatch(updateInventoryItemQty(params));
    };

    const deleteQtyFn = (mainCategoryId, subCategoryId, itemId) => {
        const params = {
            itemId: itemId,
            pathKeyId: {
                mainCategoryId: mainCategoryId,
                subCategoryId: subCategoryId,
            },
        };
        dispatch(deleteInventoryItem(params));
    };

    const updateCustomQtyFn = (operation, id) => {
        const payload = {
            id: id,
            operation: operation,
        };
        dispatch(updateCustomItemsQty(payload));
    };

    const deleteCustomQtyFn = (id) => {
        dispatch(deleteCustomItems({ id: id }));
    };

    return (
        <>
            {totalCount === 0 && (
                <Typography className={classes.emptyInventory} variant="h6" color="primary">
                    It looks like there’s no inventory added yet. Start by adding items to see them listed here.
                </Typography>
            )}
            {totalCount > 0 && (
                <List>
                    {inventoryData.get("inventory").map((category, mainIndex) => {
                        if (getCategoryCount(category) === 0) return null;

                        return category.get("category").map((subCategory, subIndex) => {
                            if (getSubCategoryCount(subCategory) === 0) return null;

                            return subCategory.get("items").map((item, itemIndex) => {
                                if (item.get("qty") === 0) return null;

                                return (
                                    <ListItem key={item.get("id")} className={classes.searchCategoryItems}>
                                        <ListItemText
                                            style={{ flex: 1 }}
                                            primary={
                                                <Typography style={{ fontWeight: 600 }}>
                                                    {item.get("displayName")}
                                                </Typography>
                                            }
                                        />
                                        <Box className={classes.iconBox}>
                                            {item.get("qty") === 1 ? (
                                                <IconButton
                                                    title="Delete Item"
                                                    size="small"
                                                    onClick={() => deleteQtyFn(category.get("id"), subCategory.get("id"), item.get("id"))}
                                                >
                                                    <Delete className={classes.iconColor} />
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    title="Remove Item"
                                                    size="small"
                                                    onClick={() => updateQtyFn("decrement", { mainIndex, subIndex, itemIndex })}
                                                >
                                                    <Remove className={classes.iconColor} />
                                                </IconButton>
                                            )}
                                            <Typography style={{ fontWeight: 600 }}>
                                                {item.get("qty")}
                                            </Typography>
                                            <IconButton
                                                title="Add Item"
                                                size="small"
                                                onClick={() => updateQtyFn("increment", { mainIndex, subIndex, itemIndex })}
                                            >
                                                <Add className={classes.iconColor} />
                                            </IconButton>
                                        </Box>
                                    </ListItem>
                                )
                            });
                        });
                    })}

                    {/* Custom Items List */}
                    {customItemscount > 0 && customItemsList.map((item, index) => (
                        <ListItem key={item.get("id")} className={classes.searchCategoryItems}>
                            <ListItemText
                                style={{ flex: 1 }}
                                primary={
                                    <>
                                        <Typography style={{ fontWeight: 600 }}>
                                            {item.get("item_name")}
                                        </Typography>
                                        <Box className={classes.customItemText}>
                                            <Typography variant="caption" color="textPrimary">
                                                L: {item.get("item_length")} Feet
                                            </Typography>
                                            <Typography variant="caption" color="textPrimary">
                                                W: {item.get("item_width")} Feet
                                            </Typography>
                                            <Typography variant="caption" color="textPrimary">
                                                H: {item.get("item_height")} Feet
                                            </Typography>
                                        </Box>
                                    </>
                                }
                            />
                            <Box className={classes.iconBox}>
                                {item.get("item_qty").toString() === "1" ? (
                                    <IconButton
                                        title="Delete Item"
                                        size="small"
                                        onClick={() => deleteCustomQtyFn(item.get("id"))}
                                    >
                                        <Delete className={classes.iconColor} />
                                    </IconButton>
                                ) : (
                                    <IconButton
                                        title="Remove Item"
                                        size="small"
                                        onClick={() => updateCustomQtyFn("decrement", item.get("id"))}
                                    >
                                        <Remove className={classes.iconColor} />
                                    </IconButton>
                                )}
                                <Typography style={{ fontWeight: 600 }}>
                                    {item.get("item_qty")}
                                </Typography>
                                <IconButton
                                    title="Add Item"
                                    size="small"
                                    onClick={() => updateCustomQtyFn("increment", item.get("id"))}
                                >
                                    <Add className={classes.iconColor} />
                                </IconButton>
                            </Box>
                        </ListItem>
                    ))}
                </List >
            )}
        </>
    );
};

export default InventoryList;